import type { ChangeEvent, FC, MouseEvent, TouchEvent } from "react";

import classNames from "classnames";

import { PauseIcon } from "@jti/brand-icons/pause";
import { PlayIcon } from "@jti/brand-icons/play";
import { ReplayIcon } from "@jti/brand-icons/replay";
import { SoundOffIcon } from "@jti/brand-icons/sound-off";
import { SoundOnIcon } from "@jti/brand-icons/sound-on";

import styles from "../VideoPlayer.module.scss";

interface ControlsProps {
  onPlayPause: () => void;
  onMute: (event: MouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>) => void;
  onFullScreen?: () => void;
  muted: boolean;
  playing: boolean;
  played: number;
  onSeekChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSeekMouseUp: (e: MouseEvent<HTMLInputElement> | TouchEvent<HTMLInputElement>) => void;
  onVolumeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  volume: number;
  elapsedTime: string;
  totalDuration: string;
  loop: boolean;
  videoEnded: boolean;
  disableSeek?: boolean;
  playLabel?: string;
  muteLabel?: string;
  soundOnLabel?: string;
  seekLabel?: string;
  volumeLabel?: string;
}

export const VideoControls: FC<ControlsProps> = ({
  onPlayPause,
  playing,
  played,
  onMute,
  muted,
  onSeekChange,
  onSeekMouseUp,
  onVolumeChange,
  volume,
  elapsedTime,
  totalDuration,
  loop,
  videoEnded = false,
  disableSeek = false,
  playLabel,
  muteLabel,
  soundOnLabel,
  seekLabel,
  volumeLabel,
}) => {
  const getPlayPauseIcon = () => {
    if (videoEnded && !loop) {
      return ReplayIcon;
    }
    return playing ? PauseIcon : PlayIcon;
  };
  const Icon = getPlayPauseIcon();
  return (
    <div className={classNames(styles.videoPlayerControls)} data-testid="videoControlsElement">
      <button
        className={styles.videoPlayerButton}
        onClick={onPlayPause}
        data-testid="videoControlsPlayPauseButton"
        aria-label={playLabel}
      >
        <Icon className={styles.iconPlayIcon} data-testid="VideoControlsPlaybackButton" />
      </button>
      <input
        type="range"
        min="0"
        max="1"
        step="any"
        value={played}
        onMouseUp={onSeekMouseUp}
        onTouchEnd={onSeekMouseUp}
        onChange={onSeekChange}
        className={styles.videoPlayerProgressBar}
        data-testid="videoControlsProgressBar"
        disabled={disableSeek}
        aria-label={seekLabel}
      />
      <div className={styles.videoPlayerTime}>
        <span className={styles.elapsedTime}>{elapsedTime}</span>
        <span className={styles.separator}>/</span>
        <span className={styles.totalDuration}>{totalDuration}</span>
      </div>
      <div className={styles.videoPlayerSound}>
        <button
          className={styles.videoPlayerButton}
          onClick={onMute}
          data-testid="videoControlsMuteButton"
          aria-label={muted ? muteLabel : soundOnLabel}
        >
          {muted ? (
            <SoundOffIcon className={styles.iconSpeaker} data-testid="VideoControlsMuteButton" />
          ) : (
            <SoundOnIcon className={styles.iconSpeaker} data-testid="VideoControlsMuteButton" />
          )}
        </button>
        <input
          type="range"
          value={muted ? 0 : volume}
          min={0}
          max={1}
          step={0.01}
          onChange={onVolumeChange}
          aria-label={volumeLabel}
        />
      </div>
    </div>
  );
};
